import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      navigate(data.user.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      if (err.response?.status === 403) {
        toast.info("Please confirm your email to continue.");
        navigate("/verify-email", { state: { email } });
        return;
      }
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your ledger."
      footer={<>No account? <Link to="/signup" className="text-olive font-semibold underline" data-testid="link-signup">Start tracking</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-slate-500 underline" data-testid="link-forgot">Forgot?</Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
          {loading ? "Logging in…" : "Log in"}
        </Button>
      </form>
      <div className="relative my-6 text-center">
        <span className="bg-background px-3 text-xs text-slate-400 relative z-10">OR</span>
        <div className="absolute inset-x-0 top-1/2 border-t border-slate-200" />
      </div>
      <Button variant="outline" className="w-full" onClick={google} data-testid="google-login">
        Continue with Google
      </Button>
    </AuthShell>
  );
}
